import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Injector, NgZone, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { filter, firstValueFrom, Subject, Subscription, take, takeUntil } from 'rxjs';
import { MapService } from '../../services/map services/map.service';
import * as mapboxgl from 'mapbox-gl';
import { GeoPoint } from '@angular/fire/firestore';
import { CameraService } from '../../services/camera/camera.service';
import { FirestoreService } from '../../services/admin services/firestore.service';
import { PhotoDetailModalComponent } from '../photo-detail-modal/photo-detail-modal.component';
import { InitializationService } from '../../services/admin services/initialization.service';
import { PhotoTimerService } from '../../services/camera/photo-timer.service';
import { Geolocation } from '@capacitor/geolocation';
import { VideoDetailModalComponent } from '../video-detail-modal/video-detail-modal.component';
import { VideoRecorderComponent } from '../video-recorder/video-recorder.component';
import { StorageService } from '../../services/admin services/storage.service';
import { AudioRecorderComponent } from '../audio-recorder/audio-recorder.component';
import { AudioDetailModalComponent } from '../audio-detail-modal/audio-detail-modal.component';
import { ContactInfoComponent } from '../contact-info/contact-info.component';
import { IDBPhotoCacheService } from '../../services/IDB/idbphoto-cache.service';
import { BeingService } from '../../services/being/being.service';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Being, KnownPlace } from '../../interfaces/being.model';
import { AudioMetadata, PhotoData, UploadEvent, VideoMetadata } from '../../interfaces/user-generated-content.model';
import { MatDialog } from '@angular/material/dialog';
import { Discovery, MediaKind, Toast } from '../../interfaces/master-interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BeingRendererService } from '../../services/being/being-renderer.service';
import { AnonymousUserService } from '../../services/user services/anonymous-user.service';
import { Auth } from '@angular/fire/auth';
import { DiscoveryRendererService } from '../../services/map services/discovery-renderer.service';
import { ObservationCirclesService } from '../../services/map services/observation-circles.service';
import { BeingHistoryComponent } from '../being-history/being-history.component';
import { BeingChatComponent } from '../being-chat/being-chat.component';
import { BeingVitalsComponent } from '../being-vitals/being-vitals.component';




@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    VideoRecorderComponent,
    ReactiveFormsModule,
    AudioRecorderComponent,
    BeingChatComponent,
    BeingVitalsComponent
    // NotionLensComponent
  ],
})

export class MapComponent implements OnInit {

  @ViewChild('skyCanvas', { static: true }) skyCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;
  @Output() minimizeModal = new EventEmitter<{ isMinimized: boolean }>();
  @ViewChild('container', { read: ViewContainerRef, static: false }) container!: ViewContainerRef;
  @ViewChild('modalContainer', { read: ViewContainerRef, static: true }) modalContainer!: ViewContainerRef;
  @ViewChild('videoRecorder') videoRecorder!: VideoRecorderComponent;
  @ViewChild('audioRecorder') audioRecorder!: AudioRecorderComponent;

  private readonly injector = inject(Injector);
  private destroyRef = inject(DestroyRef);
  private subscriptions: Subscription[] = [];
  public newPhotoAdded = new Subject<any>();
  public newVideoAdded = new Subject<any>();
  public newAudioAdded = new Subject<any>();
  private static readonly TICK_MS = 15 * 60 * 1000;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  lastPosition: { lat: number; lng: number } = { lat: 0, lng: 0 };
  currentPosition: { lat: number; lng: number } = { lat: 0, lng: 0 };
  lastUpdateTime: number = Date.now();
  tickArrivalTime: number = Date.now() + 15 * 60 * 1000;
  shouldShowBirthPrompt: boolean = true;
  isCapturingAudio = false;
  map: mapboxgl.Map | null = null;
  isMapLoading = false;
  isCapturingPhoto = false;
  photoUrl: string | null = null;
  deviceId!: string;
  private _markers: Map<string, mapboxgl.Marker> = new Map();
  isAnimating = false;
  isMapReady = false;
  private expirationSubscriptions = new Map<string, Subscription>();
  private countdownSubscriptions = new Map<string, Subscription>();
  selectedPhotoId: string | null = null;
  pendingNavigation: { lat: number, lng: number } | null = null;
  private navigationSubscription: Subscription | null = null;
  isCapturingVideo = false;
  videoUrl: string | null = null;
  currentVideoModal: any = null;
  currentUser: any = null;
  private unsubscribeFunctions: (() => void)[] = [];
  private addedMarkerIds = new Set<string>();
  isBirthing = false;
  beings: Being[] = [];
  currentBeing: any;
  hasBeings = false;
  newborn: Being | null = null;
  birthError: string | null = null;
  beingsLoaded = false;
  animationInterval: any;
  being: Being | null = null;
  followBeing = false;
  picturePlaces: KnownPlace[] = [];
  private allBeings: Being[] = [];
  toast: Toast | null = null;
  animationFrameId: number | null = null;
  destroy$ = new Subject<void>();
  discoveries: Discovery[] = [];
  currentZoom = 0;
  visibleBeingsCount = 0;
  visibleDiscoveriesCount = 0;
  aliveCount = 0;
  beingDeadMessage: string | null = null;
  deadCount = 0;
  deadUserBeings: any[] = [];
  private beingVisibilityThreshold = 14;
  private readonly _circles = inject(ObservationCirclesService);
  private readonly onMapMove = () => this.updateStats();
  private readonly onMapMoveEnd = () => this._ngZone.run(() => this.updateStats());
  isOpen = false;
  unreadCount = 0;
  private onMapClick = (e: mapboxgl.MapMouseEvent) => {
    const id = this._beingRenderer.beingAt(e.point);
    if (id) this.goToStats(id);
  };

  constructor(
    public _mapService: MapService,
    private _cameraService: CameraService,
    private _firestoreService: FirestoreService,
    private _auth: Auth,
    private _dialog: MatDialog,
    private _ngZone: NgZone,
    private _initializationService: InitializationService,
    private _IDBphotoCacheService: IDBPhotoCacheService,
    private _photoTimerService: PhotoTimerService,
    private _storageService: StorageService,
    private _beingService: BeingService,
    private _cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private _anonymousUserService: AnonymousUserService,
    private _beingRenderer: BeingRendererService,
    private _discoveryRendererService: DiscoveryRendererService
  ) {
    // console.log('[MapComponent] Constructor called');
  }

  async ngOnInit(): Promise<void> {
    this.setupBeingsSubscription();

    try {
      this._initializationService.initializationStatusSubject.next('initializing');
      this.isMapLoading = true;

      if (!this._mapService.mapInitialized.value) {
        await this.initializeMap();
      }

      // Wait for map style to load and attach all renderers
      await this.handleMapReady();

      this.isMapReady = true;  // ✅ Set AFTER style is loaded

      this.registerMapEventHandlers();
      this.subscribeToMapState();

      this._initializationService.initializationStatusSubject.next('completed');
    } catch (error) {
      console.error('[MapComponent] ❌ Error during initialization:', error);
      this._initializationService.initializationStatusSubject.next('error');
    } finally {
      this.isMapLoading = false;
    }
  }

  private setupBeingsSubscription(): void {
    let isInitialLoad = true;

    this._beingService.getCreatedBeings()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(beings => {
        this.allBeings = beings;
        this.beings = this.filterBeingsByViewport(beings);

        const currentUserId = this._anonymousUserService?.currentUserId;
        const userBeings = beings.filter(b => b.creatorDeviceId === currentUserId && b.body?.alive);
        const deadUserBeings = beings.filter(b => b.creatorDeviceId === currentUserId && !b.body?.alive);

        this.hasBeings = userBeings.length > 0;

        // Handle being state
        if (userBeings.length > 0) {
          this.currentBeing = userBeings[0];
          this.beingDeadMessage = null;
          this.deadUserBeings = [];
        } else if (deadUserBeings.length > 0) {
          const deadBeing = deadUserBeings[0];
          const cause = deadBeing.body?.causeOfDeath || 'unknown cause';
          this.beingDeadMessage = `Your being died from ${cause}. Create a new one to continue.`;
          this.deadUserBeings = deadUserBeings;
          this.currentBeing = null;
        }

        // Initial load setup
        if (isInitialLoad) {
          this.being = this.beings[0] || null;
          this.beingsLoaded = true;
          this.ensureAnimationStarts();
          isInitialLoad = false;
        }

        // Sync with renderer
        this.updateBeingStatus();
        this._beingRenderer.syncBeings(this.beings, this.allBeings);
      });
  }

  private registerMapEventHandlers(): void {
    const map = this._mapService.map;
    if (!map) {
      console.warn('[MapComponent] ⚠️ Map not available when registering handlers');
      return;
    }

    // Viewport filtering on move
    map.on('moveend', () => {
      this.beings = this.filterBeingsByViewport(this.allBeings);
      this._beingRenderer.syncBeings(this.beings, this.allBeings);
    });

    // Other handlers
    map.on('move', this.onMapMove);
    map.on('moveend', this.onMapMoveEnd);
  }

  private handleMapReady(): Promise<void> {
    return new Promise((resolve) => {
      const map = this._mapService.map;

      if (!map) {
        console.error('[handleMapReady] ❌ MAP IS NULL!');
        resolve();
        return;
      }

      const attachAll = () => {
        // Attach being renderer
        this._beingRenderer.attachToMap(map);

        // Attach circles
        this._circles.attach(map, { maxDocs: 400 });

        resolve();
      };

      // If style is already loaded, attach immediately
      if (map.isStyleLoaded()) {
        attachAll();
        return;
      }

      // Otherwise, wait for style.load event
      const onStyleLoad = () => {
        map.off('style.load', onStyleLoad);
        attachAll();
      };

      map.on('style.load', onStyleLoad);

      // Fallback: resolve after 2s even if style.load doesn't fire
      setTimeout(() => {
        map.off('style.load', onStyleLoad);
        attachAll();
      }, 2000);
    });
  }

  async ngAfterViewInit(): Promise<void> {
    this.subscribeToMapState();
    this.cleanupPhotoCache();
    this.setupBeingContentAttraction();
    this._discoveryRendererService.start();
    this.ensureAnimationStarts();
    this.destroyRef.onDestroy(() => {
      this._beingRenderer.detach();

      const m = this._mapService.map;
      if (m) {
        m.off('move', this.onMapMove);
        m.off('moveend', this.onMapMoveEnd);
      }
    });
  }


  private async initializeMap(): Promise<boolean> {
    try {
      //  console.log('[MapComponent] Starting map initialization in background');

      // Step 1: Preload map assets
      const preloadSuccess = await firstValueFrom(this._mapService.preloadMapAssets());
      if (!preloadSuccess) {
        console.error('[MapComponent] Failed to preload map assets');
        return false;
      }

      // Step 2: Initialize the map
      const mapInitialized = await firstValueFrom(this._mapService.initializeMapPostLogin());
      if (!mapInitialized) {
        console.error('[MapComponent] Failed to initialize map');
        return false;
      }

      const map = this._mapService.map;
      if (!map) {
        console.error('[MapComponent] Map missing after initialization');
        return false;
      }

      map.off('click', this.onMapClick);
      map.on('click', this.onMapClick);

      // console.log('[MapComponent] Map initialization successful');
      return true;
    } catch (error) {
      console.error('[MapComponent] Map initialization failed:', error);
      return false;
    }
  }


  private subscribeToMapState(): void {
    this._mapService.mapState$
      .pipe(
        filter(state => state === 'loaded'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.loadExistingPhotos();
        this.loadExistingVideos();
        this.loadExistingAudios();
        this.listenForNewContent();
      });
  }

  private filterBeingsByViewport(beings: Being[]): Being[] {
    if (!this._mapService.map) return beings;

    const bounds = this._mapService.map.getBounds();
    if (!bounds) return beings;

    // console.log('📍 Map bounds:', {
    //   north: bounds.getNorth(),
    //   south: bounds.getSouth(),
    //   east: bounds.getEast(),
    //   west: bounds.getWest(),
    // });

    const filtered = beings.filter(being => {
      if (!being.body.alive) return false;

      const { lat, lng } = being.position;
      const isInBounds = bounds.contains([lng, lat]);

      // console.log(`  Being [${being.id?.slice(0, 8)}] at (${lat.toFixed(4)}, ${lng.toFixed(4)}): ${isInBounds ? '✅ IN' : '❌ OUT'}`);

      return isInBounds;
    });

    return filtered;
  }




  private setupDeletionListeners(): void {
    // Subscribe to photo deletion events
    const photoDeleteSub = this._firestoreService.photoDeleted.subscribe(docId => {
      //  console.log(`[MapComponent] Photo deletion event received for: ${docId}`);
      this.removeMarkerFromMap(docId);
    });

    // Subscribe to video deletion events
    const videoDeleteSub = this._firestoreService.videoDeleted.subscribe(docId => {
      //  console.log(`[MapComponent] Video deletion event received for: ${docId}`);
      this.removeMarkerFromMap(docId);
    });

    // Subscribe to audio deletion events
    const audioDeleteSub = this._firestoreService.audioDeleted.subscribe(docId => {
      //  console.log(`[MapComponent] Audio deletion event received for: ${docId}`);
      this.removeMarkerFromMap(docId);
    });

    // Add subscriptions to cleanup array
    this.subscriptions.push(photoDeleteSub, videoDeleteSub, audioDeleteSub);
  }

  private removeMarkerFromMap(docId: string): void {
    // Get the marker from our markers map
    const marker = this._markers.get(docId);

    if (marker) {
      // Remove the marker from the map
      marker.remove();

      // Remove it from our tracked markers
      this._markers.delete(docId);

      // console.log(`[MapComponent] Marker removed: ${docId}`);
    } else {
      console.warn(`[MapComponent] Attempted to remove non-existent marker: ${docId}`);
    }
  }

  private listenForNewContent(): void {
    try {
      //  console.log('[MapComponent] Setting up content listeners');

      // Start listening for content without timestamp filtering
      this._firestoreService.listenForNewPhotos();
      this._firestoreService.listenForNewVideos();
      this._firestoreService.listenForNewAudios();

      // Set up subscriptions to content events
      this.setupPhotoSubscription();
      this.setupVideoSubscription();
      this.setupAudioSubscription();

      this.setupDeletionListeners();

      // console.log('[MapComponent] Content listeners successfully set up');
    } catch (error) {
      console.error('[MapComponent] Error setting up content listeners:', error);
    }
  }

  private setupPhotoSubscription(): void {
    const photoSub = this._firestoreService.newPhotoAdded.subscribe(photo => {
      // console.log('[MapComponent] New photo event received:', photo.id);

      // Skip if we've already added this marker
      if (this._markers.has(photo.id)) {
        //  console.log('[MapComponent] Skipping duplicate photo marker:', photo.id);
        return;
      }

      // Validate coordinates again for extra safety
      const latitude = Number(photo.latitude);
      const longitude = Number(photo.longitude);

      if (isNaN(latitude) || isNaN(longitude)) {
        console.error('[MapComponent] Invalid coordinates for photo:', photo.id,
          'Lat:', photo.latitude, 'Lng:', photo.longitude);
        return;
      }

      // console.log('[MapComponent] Adding new photo marker at:', latitude, longitude);
      this.addPhotoMarkerToMap(latitude, longitude, photo.photoUrl, photo.id);
    });

    this.subscriptions.push(photoSub);
  }

  private setupVideoSubscription(): void {
    const videoSub = this._firestoreService.newVideoAdded.subscribe(video => {
      // console.log('[MapComponent] New video event received:', video.id);

      // Skip if we've already added this marker
      if (this._markers.has(video.id)) {
        //  console.log('[MapComponent] Skipping duplicate video marker:', video.id);
        return;
      }

      // Validate coordinates for safety
      const latitude = Number(video.latitude);
      const longitude = Number(video.longitude);

      if (isNaN(latitude) || isNaN(longitude)) {
        console.error('[MapComponent] Invalid coordinates for video:', video.id,
          'Lat:', video.latitude, 'Lng:', video.longitude);
        return;
      }

      //  console.log('[MapComponent] Adding new video marker at:', latitude, longitude);
      this.addVideoMarkerToMap(latitude, longitude, video.id);
    });

    this.subscriptions.push(videoSub);
  }

  private setupAudioSubscription(): void {
    const audioSub = this._firestoreService.newAudioAdded.subscribe(audio => {
      //   console.log('[MapComponent] New audio event received:', audio.id);

      // Skip if we've already added this marker
      if (this._markers.has(audio.id)) {
        //  console.log('[MapComponent] Skipping duplicate audio marker:', audio.id);
        return;
      }

      // Validate coordinates for safety
      const latitude = Number(audio.latitude);
      const longitude = Number(audio.longitude);

      if (isNaN(latitude) || isNaN(longitude)) {
        console.error('[MapComponent] Invalid coordinates for audio:', audio.id,
          'Lat:', audio.latitude, 'Lng:', audio.longitude);
        return;
      }

      // console.log('[MapComponent] Adding new audio marker at:', latitude, longitude);
      this.addAudioMarkerToMap(latitude, longitude, audio.id);
    });

    this.subscriptions.push(audioSub);
  }

  private setupBeingContentAttraction(): void {
    const contentPlaces: Map<string, KnownPlace> = new Map();

    // Photo attraction — location is GeoPoint
    const photoSub = this._firestoreService.newPhotoAdded.subscribe((photo: PhotoData) => {
      const point = typeof photo.location === 'object'
        ? { lat: photo.location.lat, lng: photo.location.lng }
        : { lat: 0, lng: 0 }; // fallback

      contentPlaces.set(`photo:${photo.id}`, {
        point,
        kind: 'discovery' as const,
        confidence: 0.95
      });
      // console.log('🧬 Being attracted to new photo at:', point);
    });

    // Video attraction — separate lat/lng
    const videoSub = this._firestoreService.newVideoAdded.subscribe((video: VideoMetadata) => {
      contentPlaces.set(`video:${video.docId}`, {
        point: { lat: video.latitude, lng: video.longitude },
        kind: 'discovery' as const,
        confidence: 0.95
      });
      //  console.log('🧬 Being attracted to new video at:', video.latitude, video.longitude);
    });

    // Audio attraction — separate lat/lng
    const audioSub = this._firestoreService.newAudioAdded.subscribe((audio: AudioMetadata) => {
      contentPlaces.set(`audio:${audio.docId}`, {
        point: { lat: audio.latitude, lng: audio.longitude },
        kind: 'discovery' as const,
        confidence: 0.95
      });
      //  console.log('🧬 Being attracted to new audio at:', audio.latitude, audio.longitude);
    });

    // Convert Map to array for step() function
    this.picturePlaces = Array.from(contentPlaces.values());

    this.subscriptions.push(photoSub, videoSub, audioSub);
  }


  async handleButtonClickPhoto() {
    if (this.isAnimating) return;

    this.isAnimating = true; // Start rotation animation
    this.showToast('Opening camera...');

    // Wait for animation to finish before opening camera
    setTimeout(async () => {
      try {
        await this.capturePhotoAndPublish();
      } catch (error) {
        console.error('[MapComponent] Error after animation:', error);
      } finally {
        this.isAnimating = false; // Reset animation
      }
    }, 600); // Matches animation duration (0.6s)
  }

  async capturePhotoAndPublish(): Promise<void> {
    try {
      this.isCapturingPhoto = true;
      this.showToast('Getting your location for photo tagging...');

      // Get coordinates
      let latitude = 0;
      let longitude = 0;
      try {
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } catch (locationError) {
        console.error('[MapComponent] Location error:', locationError);
        this.showToast('Unable to get location. Photo will be taken without location data.');
      }

      // console.log('[MapComponent] About to open camera...');

      // ✅ Pass auth.uid to camera service
      const authUid = this._auth.currentUser?.uid;
      if (!authUid) {
        this.showToast('Authentication error - cannot save photo');
        return;
      }

      const photoResult = await this._cameraService.capturePhoto(
        latitude,
        longitude,
      );

      //console.log('[MapComponent] About to open camera...');
      //console.log('[MapComponent] Camera result:', photoResult);

      if (photoResult) {
        // Store in IDB with error handling
        let storedSuccessfully = false;
        if (photoResult.photoUrl && !photoResult.photoUrl.startsWith('data:')) {
          try {
            await this._IDBphotoCacheService.storeUrl(photoResult.docId, photoResult.photoUrl);
            const cachedUrl = await this._IDBphotoCacheService.getUrlFromCache(photoResult.docId);
            if (cachedUrl) {
              storedSuccessfully = true;
            } else {
              console.error('[MapComponent] URL storage verification failed - not found in cache');
            }
          } catch (storageError) {
            console.error('[MapComponent] Error storing URL in IDB:', storageError);
          }
        } else {
          console.warn('[MapComponent] Cannot store URL in IDB: ',
            !photoResult.photoUrl ? 'URL is empty' : 'URL is a data URL');
        }

        this.photoUrl = photoResult.photoUrl;

        if (storedSuccessfully) {
          this.showPhotoDetail(photoResult.docId, photoResult.photoUrl);
        } else {
          console.warn('[MapComponent] URL not stored in IDB, continuing with original URL');
          this.showPhotoDetail(photoResult.docId, photoResult.photoUrl);
        }

        // Fly to the photo location immediately to provide feedback to the user
        if (this._mapService.map) {
          this._mapService.map.flyTo({
            center: [photoResult.longitude, photoResult.latitude],
            zoom: 10,
            speed: 1.2
          });
        }

        this.showToast('Photo added to map!');
      }
    } catch (error: any) {
      console.error('[MapComponent] Error in photo capture process:', error);
      let errorMessage = 'Failed to capture photo. ';
      if (error?.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again.';
      }
      this.showToast(errorMessage);
    } finally {
      this.isCapturingPhoto = false;
    }
  }

  private cleanupPhotoCache(): void {
    this._IDBphotoCacheService.clearOldEntries()
      // .then(() => console.log('[MapComponent] Photo cache cleanup completed'))
      .catch(error => console.warn('[MapComponent] Error during photo cache cleanup:', error));
  }

  private addPhotoMarkerToMap(latitude: number, longitude: number, photoUrl: string, docId: string): void {
    if (this.addedMarkerIds.has(docId) && this._markers.has(docId)) return;

    if (!this._mapService.map) {
      console.warn('[MapComponent] Map not initialized, waiting...');
      setTimeout(() => this.addPhotoMarkerToMap(latitude, longitude, photoUrl, docId), 100);
      return;
    }

    const ringColor = this.getRandomColor();
    const RADIUS = 54;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

    const element = document.createElement('div');
    element.className = 'photo-marker';

    const container = document.createElement('div');
    container.className = 'countdown-container';
    container.style.position = 'relative';
    container.style.width = '50px';
    container.style.height = '50px';
    container.style.display = 'flex';
    container.style.justifyContent = 'center';
    container.style.alignItems = 'center';

    // Countdown ring — the only ring on this marker
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 120 120');
    svg.style.position = 'absolute';
    svg.style.top = '-4px';
    svg.style.left = '-4px';
    svg.style.width = '58px';
    svg.style.height = '58px';
    svg.style.zIndex = '1';
    svg.style.opacity = '0';
    svg.style.transition = 'opacity 0.5s ease-in-out';
    svg.style.pointerEvents = 'none';

    // Faint full-circle track so the marker still reads as a disc when nearly expired
    const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    track.setAttribute('cx', '60');
    track.setAttribute('cy', '60');
    track.setAttribute('r', String(RADIUS));
    track.setAttribute('fill', 'none');
    track.setAttribute('stroke', 'rgba(255,255,255,0.25)');
    track.setAttribute('stroke-width', '8');

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '60');
    circle.setAttribute('cy', '60');
    circle.setAttribute('r', String(RADIUS));
    circle.setAttribute('fill', 'none');
    circle.setAttribute('stroke', ringColor);
    circle.setAttribute('stroke-width', '8');
    circle.setAttribute('stroke-linecap', 'round');
    circle.setAttribute('stroke-dasharray', `${CIRCUMFERENCE} ${CIRCUMFERENCE}`);
    circle.setAttribute('transform', `rotate(-90 60 60)`);

    svg.appendChild(track);
    svg.appendChild(circle);
    container.appendChild(svg);

    (container as any)._updateCountdown = (pct: number) => {
      const p = Math.max(0, Math.min(100, pct));
      circle.setAttribute('stroke-dasharray', `${(p / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`);
    };

    // Thumbnail — no border, so nothing occludes the ring
    const img = document.createElement('img');
    img.src = photoUrl;
    img.style.boxSizing = 'border-box';
    img.style.width = '50px';
    img.style.height = '50px';
    img.style.border = 'none';
    img.style.borderRadius = '50%';
    img.style.objectFit = 'cover';
    img.style.position = 'relative';
    img.style.zIndex = '2';

    container.appendChild(img);
    element.appendChild(container);

    const marker = new mapboxgl.Marker(element)
      .setLngLat([longitude, latitude])
      .addTo(this._mapService.map);

    this._markers.set(docId, marker);
    this.addedMarkerIds.add(docId);

    const revealRing = () => {
      setTimeout(() => {
        svg.style.opacity = '1';
        this.setupCountdownVisualization(docId, container, 'photo');
      }, 300);
    };
    img.onload = revealRing;
    img.onerror = revealRing;

    marker.getElement().addEventListener('click', (e) => {
      this._ngZone.run(() => {
        e.stopPropagation();
        e.preventDefault();
        this.showPhotoDetail(docId, photoUrl);
      });
    });

    element.style.transform = 'scale(0)';
    requestAnimationFrame(() => {
      element.style.transition = 'transform 0.3s ease-out';
      element.style.transform = 'scale(1)';
    });

    // this._mapService.map.flyTo({
    //   center: [longitude, latitude],
    //   zoom: 10,
    //   speed: 1.2
    // });

    this.trackExpiration(docId, 'photo');
  }

  private addVideoMarkerToMap(latitude: number, longitude: number, docId: string): void {
    if (!this._mapService.map) {
      console.warn('[MapComponent] Map not initialized, waiting...');
      setTimeout(() => this.addVideoMarkerToMap(latitude, longitude, docId), 100);
      return;
    }

    const ringColor = this.getRandomColor();
    const RADIUS = 54;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

    const element = document.createElement('div');
    element.className = 'video-marker';

    const container = document.createElement('div');
    container.className = 'countdown-container';
    container.style.position = 'relative';
    container.style.width = '50px';
    container.style.height = '50px';
    container.style.display = 'flex';
    container.style.justifyContent = 'center';
    container.style.alignItems = 'center';

    // Countdown ring — the only ring on this marker
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 120 120');
    svg.style.position = 'absolute';
    svg.style.top = '-4px';
    svg.style.left = '-4px';
    svg.style.width = '58px';
    svg.style.height = '58px';
    svg.style.zIndex = '1';
    svg.style.opacity = '0';
    svg.style.transition = 'opacity 0.5s ease-in-out';
    svg.style.pointerEvents = 'none';

    // Faint full-circle track so the marker still reads as a disc when nearly expired
    const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    track.setAttribute('cx', '60');
    track.setAttribute('cy', '60');
    track.setAttribute('r', String(RADIUS));
    track.setAttribute('fill', 'none');
    track.setAttribute('stroke', 'rgba(255,255,255,0.25)');
    track.setAttribute('stroke-width', '8');

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '60');
    circle.setAttribute('cy', '60');
    circle.setAttribute('r', String(RADIUS));
    circle.setAttribute('fill', 'none');
    circle.setAttribute('stroke', ringColor);
    circle.setAttribute('stroke-width', '8');
    circle.setAttribute('stroke-linecap', 'round');
    circle.setAttribute('stroke-dasharray', `${CIRCUMFERENCE} ${CIRCUMFERENCE}`);
    circle.setAttribute('transform', `rotate(-90 60 60)`);

    svg.appendChild(track);
    svg.appendChild(circle);
    container.appendChild(svg);

    (container as any)._updateCountdown = (pct: number) => {
      const p = Math.max(0, Math.min(100, pct));
      circle.setAttribute('stroke-dasharray', `${(p / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`);
    };

    // Video icon
    const iconContainer = document.createElement('div');
    iconContainer.style.width = '50px';
    iconContainer.style.height = '50px';
    iconContainer.style.display = 'flex';
    iconContainer.style.justifyContent = 'center';
    iconContainer.style.alignItems = 'center';
    iconContainer.style.position = 'relative';
    iconContainer.style.zIndex = '2';

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('width', '28');
    icon.setAttribute('height', '28');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.innerHTML = `<path fill="${ringColor}" d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11z" />`;
    icon.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))';

    iconContainer.appendChild(icon);
    container.appendChild(iconContainer);
    element.appendChild(container);

    const marker = new mapboxgl.Marker(element)
      .setLngLat([longitude, latitude])
      .addTo(this._mapService.map);

    this._markers.set(docId, marker);

    const revealRing = () => {
      setTimeout(() => {
        svg.style.opacity = '1';
        this.setupCountdownVisualization(docId, container, 'video');
      }, 300);
    };
    revealRing();

    marker.getElement().addEventListener('click', (e) => {
      this._ngZone.run(() => {
        e.stopPropagation();
        e.preventDefault();
        this.showVideoDetail(docId);
      });
    });

    element.style.transform = 'scale(0)';
    requestAnimationFrame(() => {
      element.style.transition = 'transform 0.3s ease-out';
      element.style.transform = 'scale(1)';
    });

    this._mapService.map.flyTo({
      center: [longitude, latitude],
      zoom: 10,
      speed: 1.2
    });

    this.trackExpiration(docId, 'video');
  }

  private addAudioMarkerToMap(latitude: number, longitude: number, docId: string): void {
    if (!this._mapService.map) {
      console.warn('[MapComponent] Map not initialized, waiting...');
      setTimeout(() => this.addAudioMarkerToMap(latitude, longitude, docId), 100);
      return;
    }

    const ringColor = this.getRandomColor();
    const RADIUS = 54;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

    const element = document.createElement('div');
    element.className = 'audio-marker';

    const container = document.createElement('div');
    container.className = 'countdown-container';
    container.style.position = 'relative';
    container.style.width = '50px';
    container.style.height = '50px';
    container.style.display = 'flex';
    container.style.justifyContent = 'center';
    container.style.alignItems = 'center';

    // Countdown ring — the only ring on this marker
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 120 120');
    svg.style.position = 'absolute';
    svg.style.top = '-4px';
    svg.style.left = '-4px';
    svg.style.width = '58px';
    svg.style.height = '58px';
    svg.style.zIndex = '1';
    svg.style.opacity = '0';
    svg.style.transition = 'opacity 0.5s ease-in-out';
    svg.style.pointerEvents = 'none';

    // Faint full-circle track so the marker still reads as a disc when nearly expired
    const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    track.setAttribute('cx', '60');
    track.setAttribute('cy', '60');
    track.setAttribute('r', String(RADIUS));
    track.setAttribute('fill', 'none');
    track.setAttribute('stroke', 'rgba(255,255,255,0.25)');
    track.setAttribute('stroke-width', '8');

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '60');
    circle.setAttribute('cy', '60');
    circle.setAttribute('r', String(RADIUS));
    circle.setAttribute('fill', 'none');
    circle.setAttribute('stroke', ringColor);
    circle.setAttribute('stroke-width', '8');
    circle.setAttribute('stroke-linecap', 'round');
    circle.setAttribute('stroke-dasharray', `${CIRCUMFERENCE} ${CIRCUMFERENCE}`);
    circle.setAttribute('transform', `rotate(-90 60 60)`);

    svg.appendChild(track);
    svg.appendChild(circle);
    container.appendChild(svg);

    (container as any)._updateCountdown = (pct: number) => {
      const p = Math.max(0, Math.min(100, pct));
      circle.setAttribute('stroke-dasharray', `${(p / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`);
    };

    // Audio icon
    const iconContainer = document.createElement('div');
    iconContainer.style.width = '50px';
    iconContainer.style.height = '50px';
    iconContainer.style.display = 'flex';
    iconContainer.style.justifyContent = 'center';
    iconContainer.style.alignItems = 'center';
    iconContainer.style.position = 'relative';
    iconContainer.style.zIndex = '2';

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('width', '28');
    icon.setAttribute('height', '28');
    icon.setAttribute('viewBox', '0 0 1024 1024');
    icon.innerHTML = `<path fill="${ringColor}" d="M842 454c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8c0 140.3-113.7 254-254 254S258 594.3 258 454c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8c0 168.7 126.6 307.9 290 327.6V884H326.7c-13.7 0-24.7 14.3-24.7 32v36c0 4.4 2.8 8 6.2 8h407.6c3.4 0 6.2-3.6 6.2-8v-36c0-17.7-11-32-24.7-32H548V782.1c165.3-18 294-158 294-328.1M512 624c93.9 0 170-75.2 170-168V232c0-92.8-76.1-168-170-168s-170 75.2-170 168v224c0 92.8 76.1 168 170 168m-94-392c0-50.6 41.9-92 94-92s94 41.4 94 92v224c0 50.6-41.9 92-94 92s-94-41.4-94-92z" />`;
    icon.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))';

    iconContainer.appendChild(icon);
    container.appendChild(iconContainer);
    element.appendChild(container);

    const marker = new mapboxgl.Marker(element)
      .setLngLat([longitude, latitude])
      .addTo(this._mapService.map);

    this._markers.set(docId, marker);

    const revealRing = () => {
      setTimeout(() => {
        svg.style.opacity = '1';
        this.setupCountdownVisualization(docId, container, 'audio');
      }, 300);
    };
    revealRing();

    marker.getElement().addEventListener('click', (e) => {
      this._ngZone.run(() => {
        e.stopPropagation();
        e.preventDefault();
        this.showAudioDetail(docId);
      });
    });

    element.style.transform = 'scale(0)';
    requestAnimationFrame(() => {
      element.style.transition = 'transform 0.3s ease-out';
      element.style.transform = 'scale(1)';
    });

    this._mapService.map.flyTo({
      center: [longitude, latitude],
      zoom: 10,
      speed: 1.2
    });

    this.trackExpiration(docId, 'audio');
  }

  private setupCountdownVisualization(docId: string, containerElement: HTMLElement, kind: MediaKind): void {
    this.countdownSubscriptions.get(docId)?.unsubscribe();

    const update = (containerElement as any)._updateCountdown as ((p: number) => void) | undefined;
    if (!update) {
      console.warn('[MapComponent] No countdown updater for:', docId);
      return;
    }

    this.countdownSubscriptions.set(docId, this._photoTimerService.initTimer(docId, kind).subscribe(update));
  }

  private trackExpiration(docId: string, kind: MediaKind): void {
    this.expirationSubscriptions.get(docId)?.unsubscribe();

    const sub = this._photoTimerService.initTimer(docId, kind)
      .pipe(filter(p => p <= 0))
      .subscribe(() => { /* ...as before, but branch the delete on kind */ });

    this.expirationSubscriptions.set(docId, sub);
  }



  private getRandomColor(): string {
    // Method 1: Using HSL for vibrant, visually distinct colors
    const hue = Math.floor(Math.random() * 360); // 0-359 degrees on color wheel
    return `hsl(${hue}, 70%, 60%)`; // 70% saturation, 60% lightness for visibility

    // Alternative methods:

    // Method 2: Using hex colors (RGB)
    // return `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`;

    // Method 3: Using predefined colors array for more control
    // const colors = ['#FF5733', '#33FF57', '#3357FF', '#F033FF', '#FF3333', '#33FFF3', '#F3FF33'];
    // return colors[Math.floor(Math.random() * colors.length)];
  }

  private removePhotoMarker(docId: string): void {
    //console.log('[MapComponent] Attempting to remove marker for photo:', docId);

    // Check if we have this marker stored
    if (this._markers.has(docId)) {
      const marker = this._markers.get(docId);

      // Add null/undefined check to satisfy TypeScript
      if (marker) {
        // Animate marker removal
        const element = marker.getElement();
        element.style.transition = 'transform 0.3s ease-in';
        element.style.transform = 'scale(0)';

        // Remove marker after animation completes
        setTimeout(() => {
          marker.remove();
          this._markers.delete(docId);
          //console.log('[MapComponent] Marker removed for photo:', docId);
        }, 300);
      } else {
        console.warn('[MapComponent] Marker reference is undefined for photo:', docId);
        // Clean up the map entry anyway
        this._markers.delete(docId);
      }

      // Clean up subscriptions
      if (this.countdownSubscriptions.has(docId)) {
        this.countdownSubscriptions.get(docId)?.unsubscribe();
        this.countdownSubscriptions.delete(docId);
      }

      if (this.expirationSubscriptions.has(docId)) {
        this.expirationSubscriptions.get(docId)?.unsubscribe();
        this.expirationSubscriptions.delete(docId);
      }

      // Clean up the timer in the service
      this._photoTimerService.removePhotoTimer(docId);
    } else {
      console.warn('[MapComponent] No marker found for photo:', docId);
    }
  }

  private removeVideoMarker(docId: string): void {
    //console.log('[MapComponent] Attempting to remove marker for photo:', docId);

    // Check if we have this marker stored
    if (this._markers.has(docId)) {
      const marker = this._markers.get(docId);

      // Add null/undefined check to satisfy TypeScript
      if (marker) {
        // Animate marker removal
        const element = marker.getElement();
        element.style.transition = 'transform 0.3s ease-in';
        element.style.transform = 'scale(0)';

        // Remove marker after animation completes
        setTimeout(() => {
          marker.remove();
          this._markers.delete(docId);
          //console.log('[MapComponent] Marker removed for photo:', docId);
        }, 300);
      } else {
        console.warn('[MapComponent] Marker reference is undefined for photo:', docId);
        // Clean up the map entry anyway
        this._markers.delete(docId);
      }

      // Clean up subscriptions
      if (this.countdownSubscriptions.has(docId)) {
        this.countdownSubscriptions.get(docId)?.unsubscribe();
        this.countdownSubscriptions.delete(docId);
      }

      if (this.expirationSubscriptions.has(docId)) {
        this.expirationSubscriptions.get(docId)?.unsubscribe();
        this.expirationSubscriptions.delete(docId);
      }

      // Clean up the timer in the service
      this._photoTimerService.removePhotoTimer(docId);
    } else {
      console.warn('[MapComponent] No marker found for photo:', docId);
    }
  }

  private async showPhotoDetail(docId: string, photoUrl: string): Promise<void> {
    try {
      // Get high-quality URL from the cache service
      const highQualityUrl = await this._IDBphotoCacheService.getHighQualityUrl(docId, photoUrl);

      // Get the photo document from Firestore
      let photoData = await this._firestoreService.getDocById(docId);

      // Get current user ID
      let currentUserId = this._anonymousUserService.currentUserId;
      let displayName = this._anonymousUserService.userDisplayName;

      // Check if current user is the publisher
      const isPublisher = photoData &&
        photoData.userId &&
        typeof photoData.userId === 'string' &&
        photoData.userId === currentUserId;


      const dialogRef = this._dialog.open(PhotoDetailModalComponent, {
        width: '90vw',
        maxWidth: '600px',
        data: {
          photoId: docId,
          displayName: displayName,
          photoUrl: highQualityUrl,
          isPublisher: isPublisher,
          userId: currentUserId,
          expiresIn: 24 * 60 * 60 * 1000,  // ← Add this (24 hours default)
        },
        panelClass: 'photo-detail-modal'
      });

      // Handle dialog close
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (result.deleted) {
            setTimeout(() => {
              this.removePhotoMarker(docId);
              this.showToast('Photo deleted successfully!');

              if (this.map) {
                this.map.resize();
              }
            }, 200);
          } else if (result.liked !== undefined) {
            const action = result.liked ? 'liked' : 'disliked';
            this.showToast(`You ${action} this photo!`);
          }
        }
      });

    } catch (error) {
      console.error('[MapComponent] Error showing photo details:', error);
      this.showToast('Could not load photo details');
    }
  }

  handleButtonClickVideo(): void {
    this.isCapturingVideo = true;

    // We need to wait for the component to be rendered before accessing it
    setTimeout(() => {
      if (this.videoRecorder) {
        this.videoRecorder.startCamera();
      }
    }, 0);
  }

  handleButtonClickAudio() {
    this.isCapturingAudio = true;

    setTimeout(() => {
      if (this.videoRecorder) {
        this.videoRecorder.startRecording();
      }
    }, 0);
  }


  cancelVideoCapture(): void {
    if (this.videoRecorder) {
      this.videoRecorder.cleanup();
    }
    this.isCapturingVideo = false;
  }

  async onRecordingComplete(data: { blob: Blob, url: string }): Promise<void> {
    try {
      let latitude = 0;
      let longitude = 0;
      // Get location (unchanged)
      try {
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } catch (geoError) {
        console.warn('[onRecordingComplete] Failed to get location:', geoError);
      }

      // Create a FormData object with the blob
      const formData = new FormData();
      // Create a File from the Blob (MediaRecorder provides a webm format)
      const videoFile = new File([data.blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
      formData.append('video', videoFile);

      // IMPORTANT: Await the promise to get the Observable
      const uploadObservable = await this._storageService.uploadVideoToStorage(formData, latitude, longitude);

      // Now you can subscribe to the Observable
      uploadObservable.subscribe({
        next: async (event: UploadEvent) => {
          // Only process complete events with URLs
          if (event.type === 'complete' && event.url) {
            const permanentVideoUrl = event.url;

            // Get the user ID
            let userId = this._anonymousUserService.currentUserId;

            // Save video metadata with the permanent URL
            const docId = await this._firestoreService.saveVideoMetadata(
              permanentVideoUrl,
              latitude,
              longitude,
              userId
            );

            // Set expiration time
            const expirationTime = new Date();
            try {
              await this._firestoreService.setVideoExpiration(docId, expirationTime);
            } catch (expirationError) {
              console.error('[onRecordingComplete] Error setting video expiration:', expirationError);
            }

            // this.addVideoMarkerToMap(
            //   latitude,
            //   longitude,
            //   docId
            // );

            // console.log('[onRecordingComplete] Video metadata stored successfully with ID:', docId);
          }

          this.isCapturingVideo = false;
        },
        error: (error) => {
          console.error('[onRecordingComplete] Error uploading video:', error);
          this.isCapturingVideo = false;
        },
        complete: () => {
          // This may not get called as we're only looking for the 'complete' event
        }
      });

    } catch (error) {
      console.error('[onRecordingComplete] Error processing video:', error);
      this.isCapturingVideo = false;
    }
  }

  //--======================================================================================--//
  //--======================================================================================--//
  //--======================================================================================--//

  async showVideoDetail(videoId: string): Promise<void> {
    try {
      // Get video metadata from service
      const videoMetadata = await this._firestoreService.getVideoMetadata(videoId);
      if (!videoMetadata) {
        throw new Error('Video metadata not found');
      }

      // Determine the video URL to use
      let videoUrl: string | undefined | null = videoMetadata.videoUrl;

      if (!videoUrl) {
        throw new Error('Unable to retrieve video URL');
      }

      // Get current user ID
      let currentUserId = this._anonymousUserService.currentUserId;

      // Check if the current user is the publisher
      const isPublisher = videoMetadata.userId === currentUserId;

      const dialogRef = this._dialog.open(VideoDetailModalComponent, {
        width: '90vw',
        maxWidth: '600px',
        data: {
          videoId: videoId,
          deviceId: this.deviceId,
          videoUrl: videoUrl,
          isPublisher: isPublisher,
          userId: currentUserId,
          expiresIn: 24 * 60 * 60 * 1000,  // ← Add this
        },
        panelClass: 'video-modal'
      });

      // Handle dialog close
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (result.deleted) {
            setTimeout(() => {
              this.removeVideoMarker(videoId);
              this.showToast('Video deleted successfully!');
              if (this.map) {
                this.map.resize();
              }
            }, 200);
          } else if (result.liked !== undefined) {
            const action = result.liked ? 'liked' : 'disliked';
            this.showToast(`You ${action} this video!`);
          }
        }
      });

    } catch (error) {
      console.error('[MapComponent] Error showing video detail:', error);
      this.showToast('Failed to load video details. Please try again.');
    }
  }

  goToStats(beingId: string) {
    this._dialog.open(BeingHistoryComponent, {
      width: '90vw',
      maxWidth: '900px',
      maxHeight: '85vh',
      panelClass: 'being-stats-modal',
      autoFocus: false,
      data: { beingId },
    });
  }


  goToContact() {
    this._dialog.open(ContactInfoComponent, {
      width: '90vw',
      maxWidth: '500px',
      panelClass: 'contact-modal-class'
    });
  }

  private async loadExistingPhotos(): Promise<void> {
    try {
      // console.log('[MapComponent] Attempting to fetch recent photos...');
      const querySnapshot = await this._firestoreService.getRecentPhotos(undefined, true);

      // console.log(`[MapComponent] Fetched ${querySnapshot.size} recent photos`);

      // Add each photo to the map
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const location = data['location'] as GeoPoint;

        if (location && data['photoUrl']) {
          this.addPhotoMarkerToMap(
            location.latitude,
            location.longitude,
            data['photoUrl'],
            doc.id
          );
          // console.log(`[MapComponent] Added photo marker to map: ${doc.id}`);
        }
      });

      // console.log('[MapComponent] Finished loading existing photos');
    } catch (error) {
      console.error('[MapComponent] Error loading existing photos:', error);
    }
  }


  async loadExistingVideos(): Promise<void> {
    try {
      // Use FirestoreService to fetch videos
      const querySnapshot = await this._firestoreService.getRecentVideos(undefined, true);

      // Add each video to the map
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const location = data['location'] as GeoPoint;

        if (location && data['videoUrl']) {
          this.addVideoMarkerToMap(
            location.latitude,
            location.longitude,
            //data['videoUrl'],
            doc.id
          );
        }
      });

      // console.log('[MapComponent] Loaded existing videos:', querySnapshot.size);
    } catch (error) {
      console.error('[MapComponent] Error loading existing videos:', error);
    }
  }


  //--==============================================================================--//
  //--==============================================================================--//
  //--==============================================================================--//




  async loadExistingAudios(): Promise<void> {
    try {
      // Use FirestoreService to fetch audios
      const querySnapshot = await this._firestoreService.getRecentAudios(undefined, true);

      // Add each audio to the map
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const location = data['location'] as GeoPoint;

        if (location && data['audioUrl']) {
          this.addAudioMarkerToMap(
            location.latitude,
            location.longitude,
            //data['audioUrl'],
            doc.id
          );
        }
      });

      // console.log('[MapComponent] Loaded existing audios:', querySnapshot.size);
    } catch (error) {
      console.error('[MapComponent] Error loading existing audios:', error);
    }
  }

  onRecordingStopped() {
    // console.log('Recording stopped');
    // Handle recording stop (e.g., UI updates)
  }

  async onAudioRecordingComplete(data: { blob: Blob, url: string }) {
    try {
      let latitude = 0;
      let longitude = 0;

      // Get location
      try {
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } catch (geoError) {
        console.warn('[onAudioRecordingComplete] Failed to get location:', geoError);
      }

      // Create a FormData object with the blob
      const formData = new FormData();

      // Create a File from the Blob (Different MIME type for audio)
      const audioFile = new File([data.blob], `audio_${Date.now()}.wav`, { type: 'audio/wav' });
      formData.append('audio', audioFile);

      // IMPORTANT: Await the promise to get the Observable
      // Assuming you have an audioStorage service or you'll adapt the existing one
      const uploadObservable = await this._storageService.uploadAudioToStorage(formData, latitude, longitude);

      // Now you can subscribe to the Observable
      uploadObservable.subscribe({
        next: async (event: UploadEvent) => {
          // Only process complete events with URLs
          if (event.type === 'complete' && event.url) {
            const permanentAudioUrl = event.url;

            // Get the user ID
            let userId = this._anonymousUserService.currentUserId;

            // Save audio metadata with the permanent URL
            // Either create a new method or adapt the existing one
            const docId = await this._firestoreService.saveAudioMetadata(
              permanentAudioUrl,
              latitude,
              longitude,
              userId
            );

            // Set expiration time
            const expirationTime = new Date();
            try {
              await this._firestoreService.setAudioExpiration(docId, expirationTime);
            } catch (expirationError) {
              console.error('[onAudioRecordingComplete] Error setting audio expiration:', expirationError);
            }

            // Add audio marker to map (assuming similar functionality as video)
            this.addAudioMarkerToMap(
              latitude,
              longitude,
              docId
            );

            // console.log('[onAudioRecordingComplete] Audio metadata stored successfully with ID:', docId);
          }

          this.isCapturingAudio = false;
        },
        error: (error) => {
          console.error('[onAudioRecordingComplete] Error uploading audio:', error);
          this.isCapturingAudio = false;
        },
        complete: () => {
          // This may not get called as we're only looking for the 'complete' event
        }
      });
    } catch (error) {
      console.error('[onAudioRecordingComplete] Error processing audio:', error);
      this.isCapturingAudio = false;
    }
  }



  async showAudioDetail(audioId: string): Promise<void> {
    try {
      // Get audio metadata from service
      const audioMetadata = await this._firestoreService.getAudioMetadata(audioId);
      if (!audioMetadata) {
        throw new Error('audio metadata not found');
      }

      // Determine the audio URL to use
      let audioUrl: string | undefined | null = audioMetadata.audioUrl;

      if (!audioUrl) {
        throw new Error('Unable to retrieve audio URL');
      }

      // Get current user ID
      let currentUserId = this._anonymousUserService.currentUserId;

      // Check if the current user is the publisher
      const isPublisher = audioMetadata.userId === currentUserId;

      // Open the dialog
      const dialogRef = this._dialog.open(AudioDetailModalComponent, {
        width: '90vw',
        maxWidth: '600px',
        data: {
          audioId: audioId,
          deviceId: this.deviceId,
          audioUrl: audioUrl,
          isPublisher: isPublisher,
          userId: currentUserId,
        },
        panelClass: 'audio-modal'
      });

      // Handle dialog close
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (result.deleted) {
            setTimeout(() => {
              this.removeAudioMarker(audioId);
              this.showToast('audio deleted successfully!');
              if (this.map) {
                this.map.resize();
              }
            }, 200);
          } else if (result.liked !== undefined) {
            const action = result.liked ? 'liked' : 'disliked';
            this.showToast(`You ${action} this audio!`);
          }
        }
      });

    } catch (error) {
      console.error('[MapComponent] Error showing audio detail:', error);
      this.showToast('Failed to load audio details. Please try again.');
    }
  }

  private removeAudioMarker(docId: string): void {
    //console.log('[MapComponent] Attempting to remove marker for photo:', docId);

    // Check if we have this marker stored
    if (this._markers.has(docId)) {
      const marker = this._markers.get(docId);

      // Add null/undefined check to satisfy TypeScript
      if (marker) {
        // Animate marker removal
        const element = marker.getElement();
        element.style.transition = 'transform 0.3s ease-in';
        element.style.transform = 'scale(0)';

        // Remove marker after animation completes
        setTimeout(() => {
          marker.remove();
          this._markers.delete(docId);
          //console.log('[MapComponent] Marker removed for photo:', docId);
        }, 300);
      } else {
        console.warn('[MapComponent] Marker reference is undefined for photo:', docId);
        // Clean up the map entry anyway
        this._markers.delete(docId);
      }

      // Clean up subscriptions
      if (this.countdownSubscriptions.has(docId)) {
        this.countdownSubscriptions.get(docId)?.unsubscribe();
        this.countdownSubscriptions.delete(docId);
      }

      if (this.expirationSubscriptions.has(docId)) {
        this.expirationSubscriptions.get(docId)?.unsubscribe();
        this.expirationSubscriptions.delete(docId);
      }

      // Clean up the timer in the service
      this._photoTimerService.removePhotoTimer(docId);
    } else {
      console.warn('[MapComponent] No marker found for photo:', docId);
    }
  }

  cancelAudioCapture(): void {
    if (this.audioRecorder) {
      this.audioRecorder.cleanup();
    }
    this.isCapturingAudio = false;
  }

  private showToast(message: string): void {
    this._snackBar.open(message, 'Close', {
      duration: 2000,
      verticalPosition: 'bottom',
      horizontalPosition: 'center',
      panelClass: ['app-toast']
    });
  }



  async birthBeing(): Promise<void> {
    if (this.isBirthing) return;
    this.isBirthing = true;
    const map = this._mapService.map;
    if (!map) return;

    try {
      const being = await firstValueFrom(this._beingService.createBeing());

      this.allBeings = [...this.allBeings, being];
      this.beings = this.filterBeingsByViewport(this.allBeings);

      // Set hasBeings based on total beings, not viewport-filtered ones
      this.hasBeings = this.allBeings.length > 0;

      this.being = being;
      this.newborn = being;

      // console.log('🧬 Newborn added to map:', being.name);
      this._cdr.markForCheck(); // Ensure change detection runs
      this.ensureAnimationStarts();

    } catch (err) {
      console.error('🧬 Birth failed:', err);
      this.birthError = 'No habitable ground found. Try again.';
    } finally {
      this.isBirthing = false;
    }
  }

  confirmName(raw: string): void {
    const name = raw.trim();
    if (!this.newborn) return;

    if (!name || name.length > 24) {
      this.newborn = null;   // fall back to the generated phonetic name
      return;
    }

    this._beingService.renameBeing(this.newborn.id, name)
      .subscribe({
        next: () => this.newborn = null,
        error: err => console.error('🧬 Rename failed:', err)
      });
  }



  //--=========================================================================================================--//
  //--=========================================================================================================--//
  //--=========================================================================================================--//
  //--=========================================================================================================--//



  /** Mix a hex colour toward white (amount > 0) or black (amount < 0). */
  shadeHex(hex: string, amount: number): string {
    const n = parseInt(hex.slice(1), 16);
    const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(c =>
      amount >= 0 ? c + (255 - c) * amount : c * (1 + amount)
    );
    return '#' + ch.map(c => Math.round(c).toString(16).padStart(2, '0')).join('').toUpperCase();
  }


  closePrompt(): void {
    // Option 1: Hide the prompt by setting a flag
    this.shouldShowBirthPrompt = false; // Add this variable to your component

    // Option 2: If you want to dismiss it permanently
    // this.hasBeings = true; // This would hide it via the *ngIf condition

    // Option 3: If you want to redirect or perform other actions
    // this.router.navigate(['/some-other-page']);
  }

  async zoomToBeing(): Promise<void> {
    // console.log('🎯 Step 1: Initial checks passed');

    if (!this._mapService.map) return;

    const currentUserId = this._anonymousUserService.currentUserId;
    // console.log('🎯 Step 2: currentUserId', currentUserId);
    if (!currentUserId) {
      console.warn('⚠️ No current user');
      return;
    }

    // Always filter for user's beings (don't rely on cached this.being)
    const userBeings = this.allBeings?.filter(b => b.creatorDeviceId === currentUserId) || [];

    if (!userBeings.length) {
      // console.log('🌍 User has no beings. Opening creation modal...');
      this.shouldShowBirthPrompt = true;
      this.birthBeing();
      return;
    }

    const currentBeing = this.being && userBeings.find(b => b.id === this.being!.id);
    const being = currentBeing || userBeings[0];

    // console.log('🎯 Step 3: Being selected', { beingId: being.id, creatorId: being.creatorDeviceId });

    const { lng, lat } = being.position;
    //  console.log('🎯 Step 4: Position validation', { lng, lat, lngValid: !isNaN(lng), latValid: !isNaN(lat) });

    if (lng === undefined || lat === undefined || isNaN(lng) || isNaN(lat)) {
      console.error('❌ Invalid position:', { lng, lat });
      return;
    }

    // console.log('✅ Flying to own being', { beingId: being.id, lng, lat });

    this._mapService.map.flyTo({
      center: [lng, lat],
      zoom: 12,
      duration: 1000,
      pitch: 45,
    });
  }


  private ensureAnimationStarts(): void {
    //  console.log('🚀 ensureAnimationStarts() called, being:', this.being?.id?.slice(0, 8));

    if (this._mapService.mapInitialized.value) {
      // console.log('✅ Map already initialized');
      if (this.being) {
        this.startAnimation();
      } else {
        console.warn('❌ No being to animate');
      }
      return;
    }

    // Map not ready yet—listen for it
    const mapReadySub = this._mapService.mapInitialized
      .pipe(
        filter(ready => ready === true),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.being) {
          this._beingRenderer.updatePosition(
            this.being.id,
            this.being.position.lat,
            this.being.position.lng
          );
          this.startAnimation();
        }
      });
  }


  /** Rejects null/undefined, NaN, and the 0,0 sentinel. */
  private validPos(p: any): boolean {
    return !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0);
  }

  startAnimation(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    if (!this._mapService.map) {
      this.retryTimer = setTimeout(() => this.startAnimation(), 500);
      return;
    }

    if (!this.being?.id) return;

    let hasReceivedFirstUpdate = false;

    this._beingService.getBeing(this.being.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedBeing) => {
          if (!updatedBeing) {
            const deadId = this.being?.id;
            this.being = null;
            if (deadId) this._beingRenderer.removeBeing(deadId);
            return;
          }

          if (!this.validPos(updatedBeing.position)) {
            console.warn(`⚠️ [${updatedBeing.name}] emit has no usable position`, updatedBeing.position);
            return;                                  // don't poison the interpolation
          }

          // Glide from where we currently are; snap into place on the first good emit.
          this.lastPosition = this.validPos(this.currentPosition)
            ? { ...this.currentPosition }
            : { ...updatedBeing.position };
          this.currentPosition = { ...updatedBeing.position };
          this.lastUpdateTime = Date.now();
          this.tickArrivalTime = Date.now() + MapComponent.TICK_MS;

          this.being = updatedBeing;
          hasReceivedFirstUpdate = true;
          this._cdr.markForCheck();
          this._beingRenderer.upsertBeing(updatedBeing);
        },
        error: (err) => console.error('❌ Being listener error:', err),
        complete: () => console.warn(`⚠️ [${this.being?.name}] Subscription completed`)
      });

    const startRAF = () => {
      if (!hasReceivedFirstUpdate) {
        this.retryTimer = setTimeout(startRAF, 50);
        return;
      }

      const animate = () => {
        if (!this.being?.id || !this.validPos(this.lastPosition) || !this.validPos(this.currentPosition)) {
          this.animationFrameId = requestAnimationFrame(animate);
          return;                                    // keep the loop alive, skip this frame
        }

        const nowMs = Date.now();
        const duration = Math.max(1, this.tickArrivalTime - this.lastUpdateTime);
        const progress = Math.min(1, Math.max(0, (nowMs - this.lastUpdateTime) / duration));

        const lat = this.lastPosition.lat + (this.currentPosition.lat - this.lastPosition.lat) * progress;
        const lng = this.lastPosition.lng + (this.currentPosition.lng - this.lastPosition.lng) * progress;

        this._beingRenderer.updatePosition(this.being.id, lat, lng);

        if (this.followBeing) this._mapService.map?.setCenter([lng, lat]);

        this.animationFrameId = requestAnimationFrame(animate);
      };

      this.animationFrameId = requestAnimationFrame(animate);
    };

    startRAF();
  }



  toggleGraticuleVisibility(): void {
    // console.log('GRID')
    this._mapService.toggleGraticule();
  }

  adjustGraticuleSubtlety(subtlety: number): void {
    const s = Math.max(0, Math.min(1, subtlety));
    this._mapService.updateGraticuleStyle({
      minorOpacity: 0.18 * s,
      majorOpacity: 0.35 * s,
      axisOpacity: 0.9 * s,
      glowOpacity: 0.22 * s
    });
  }

  private updateStats(): void {
    if (!this._mapService.map) return;

    this.currentZoom = this._mapService.map.getZoom();

    this.updateVisibleBeingsCount();

    // Load discoveries for current viewport — renderer handles the rendering,
    // the signal subscription handles populating this.discoveries
    const geohashLevel = this.getGeohashLevelForZoom(this.currentZoom);
    this._discoveryRendererService.loadDiscoveriesByZoom(geohashLevel);

    this.updateVisibleDiscoveriesCount();
  }

  private getGeohashLevelForZoom(zoom: number): number {
    if (zoom < 5) return 2;
    if (zoom < 8) return 3;
    if (zoom < 11) return 4;
    if (zoom < 14) return 5;
    if (zoom < 16) return 6;
    return 7;
  }

  private updateVisibleBeingsCount(): void {
    if (this.currentZoom >= this.beingVisibilityThreshold) {
      this.visibleBeingsCount = this.allBeings.length;
    } else {
      this.visibleBeingsCount = 0;
    }
  }

  private updateVisibleDiscoveriesCount(): void {
    // Read the signal directly
    this.discoveries = this._discoveryRendererService.discoveries();

    const map = this._mapService.getMapIfReady();
    if (!map || this.discoveries.length === 0) {
      this.visibleDiscoveriesCount = 0;
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) {
      this.visibleDiscoveriesCount = 0;
      return;
    }

    this.visibleDiscoveriesCount = this.discoveries.filter(d => {
      return bounds.contains([d.location.lng, d.location.lat]);
    }).length;
  }

  private updateBeingStatus(): void {
    this.aliveCount = this.allBeings.filter(b => b.body.alive).length;
    this.deadCount = this.allBeings.length - this.aliveCount;
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
  }

  ngOnDestroy() {

    this.unsubscribeFunctions.forEach(unsub => unsub());

    // Clean up countdown subscriptions
    this.countdownSubscriptions.forEach(sub => sub.unsubscribe());
    this.countdownSubscriptions.clear();

    // Clean up expiration subscriptions
    this.expirationSubscriptions.forEach(sub => sub.unsubscribe());
    this.expirationSubscriptions.clear();

    this._markers.forEach(marker => marker.remove());
    this._markers.clear();

    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    if (this.animationInterval) clearInterval(this.animationInterval);

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
    }

    this.destroyRef.onDestroy(() => this._circles.detach());

    const map = this._mapService.getMapIfReady();
    if (map) {
      this.discoveries.forEach(d => {
        try {
          if (map.getLayer(`discovery-marker-${d.id}`)) {
            map.removeLayer(`discovery-marker-${d.id}`);
            map.removeSource(`discovery-marker-${d.id}`);  // ← May have OTHER dependent layers!
          }
        } catch (e) { }
      });
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}